import { defineStore } from 'pinia';
import Auth from '@/services/auth';
import { normalizeError } from '@/utils/error-handler';

export const useRegisterStore = defineStore('register', {
    state: () => ({
        step: 1,
        errors: [],
        formErrors: {},
        agreeToTerms: false,
        enable2fa: false,
        twoFactorType: '',
        user: {},
        sendingCode: false,
        showResendCode: true,
        resending: false,
        resendCodeMessage: 'Resending code ...',
        otpCode: '',
        qrCode: '',
        recoveryCodes: [],
        hideVerifyCodeActions: false,
        showQrCode: false,
        copied: false,
        downloaded: false,
        showCancel2faModal: false,
        registerCreds: {
            name: '',
            email: '',
            password: '',
            password_confirmation: '',
            twofa_enabled: false,
            twofa_google_enabled: false,
            twofa_agreed: false,
            pref_allow_general_marketing: true,
            phone: '',
        },
        utmParams: {},
    }),
    getters: {},
    actions: {
        setFormError(key, message) {
            this.formErrors[key] = message;
        },
        removeFormError(key) {
            delete this.formErrors[key];
        },
        register() {
            this.validate();

            if (this.errors.length) {
                return;
            }

            this.loading = true;
            const auth = Auth.register(this.registerCreds);
            auth.then((response) => {
                if (response.status && response.status > 201) {
                    this.handleHttpStatus(response.response);
                    this.loading = false;
                    return;
                }

                if (response.data?.token) {
                    localStorage.setItem('_token', response.data.token);
                }
                this.user = response.data;

                if (this.enable2fa) {
                    this.step = 2;
                    this.loading = false;
                } else {
                    if (import.meta.env.VITE_APP_ENV === 'production') {
                        woopra.identify({
                            email: response.data.email,
                            name: response.data.name,
                            uuid: response.data.uuid,
                        });
                        woopra.track('register-my10x', {
                            name: response.data.name,
                            email: response.data.email,
                            uuid: response.data.uuid,
                        });

                        // Track with Facebook Pixel and get event_id
                        if (typeof fbq !== 'undefined') {
                            // Track the event
                            fbq('track', 'CompleteRegistration');

                            // Get the event_id from the most recent event
                            const event_id = fbq.getEventID();

                            // Make a request to our server with the event_id
                            fetch('/api/facebook-pixel/track', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                    event: 'CompleteRegistration',
                                    event_id: event_id,
                                    user_data: {
                                        external_id: response.data.uuid,
                                        em: response.data.email,
                                        fn: response.data.name.split(' ')[0], // First name
                                        ln:
                                            response.data.name.split(' ')[1] ||
                                            '', // Last name
                                    },
                                }),
                            });
                        }
                    }

                    location.href = '/welcome';
                }
                this.loading = false;
            }).catch((e) => {
                console.log(e);
                this.loading = false;
                this.handleHttpStatus(e.response);
            });
        },
        validate() {
            this.errors = [];
            if (this.passwordsMatchMessage) {
                this.errors.push({ message: 'Passwords must match.' });
            }

            if (
                this.registerCreds.twofa_enabled &&
                (this.registerCreds.phone === '' || !this.registerCreds.phone)
            ) {
                this.errors.push({
                    message: 'Please enter your phone number.',
                });
            }

            if (
                this.registerCreds.twofa_enabled &&
                !this.registerCreds.twofa_agreed
            ) {
                this.errors.push({
                    message: 'You must agree to the 2FA terms.',
                });
            }

            if (this.registerCreds.twofa_enabled) {
                if (this.registerCreds.phone.length !== 14) {
                    this.errors.push({
                        message: 'Please enter a valid phone number.',
                    });
                }
            }

            if (!this.agreeToTerms) {
                this.errors.push({
                    message:
                        'Please agree to the terms of service and privacy policy.',
                });
            }
        },
        setTwoFactorType(val) {
            this.twoFactorType = val;

            if (this.twoFactorType === 'sms') {
                this.registerCreds.twofa_enabled = true;
            } else {
                this.registerCreds.twofa_enabled = false;
            }

            this.hideVerifyCodeActions = false;
            if (this.twoFactorType === 'totp') {
                this.registerCreds.twofa_google_enabled = true;
                this.enableTotp();
            } else {
                this.registerCreds.twofa_google_enabled = false;
            }
        },
        gotoStep(step) {
            this.validateStep();
            if (this.errors.length) {
                return;
            }

            if (step === 4) {
                Auth.enableSms2fa(this.registerCreds.phone)
                    .then(() => {
                        this.step = step;
                    })
                    .catch((e) => {
                        this.errors.push({
                            message:
                                'There was a problem enabling 2FA. Please try again.',
                        });
                    });
            } else {
                this.step = step;
            }
        },
        cancelTwoFactor() {
            location.href = '/welcome';
        },
        validateStep() {
            this.errors = [];
            if (
                this.step === 2 &&
                (this.twoFactorType === '' ||
                    !this.twoFactorType ||
                    this.twoFactorType.length === 0)
            ) {
                this.errors.push({ message: 'Please choose a 2FA type.' });
            }

            if (this.step === 3 && this.twoFactorType === 'sms') {
                if (!this.registerCreds.twofa_agreed) {
                    this.errors.push({
                        message: 'Please agree to the 2FA terms.',
                    });
                }

                if (this.registerCreds.phone.length < 14) {
                    this.errors.push({
                        message: 'Please enter a valid phone number.',
                    });
                }
            }
        },
        validatePhoneNumber(code) {
            this.errors = [];
            this.validateCode(code);

            if (code.toString().length === 6) {
                const auth = Auth.validatePhoneNumber({ code: code });
                auth.then((response) => {
                    if (!response.data.valid_code) {
                        this.errors.push({
                            message:
                                'The code is invalid or expired. You may resend it.',
                        });
                        this.showResendCode = true;
                        this.sendingCode = false;
                    } else {
                        localStorage.setItem('_token', response.data.token);
                        location.href = '/welcome';
                    }
                }).catch((e) => {
                    this.handleHttpStatus(e.response);
                    this.sendingCode = false;
                });
            }
        },
        validateCode(code) {
            if (code.toString().length !== 6) {
                this.errors.push({
                    message: 'The SMS code needs to be 6 characters.',
                });
            }
        },
        async resendSmsCode() {
            this.resending = true;
            const auth = Auth.resendSmsTwoFA(this.registerCreds.phone);
            auth.then((response) => {
                if (!response.data.success) {
                    if (response.data.message === 'please_wait') {
                        message =
                            'Too many attempts. Please wait 5 min before trying again.';
                    }
                    if (response.data.message === 'too_many_tries') {
                        message =
                            'Account is locked. Too many failed attempts.';
                    }

                    this.code = '';
                    this.showResendCode = false;
                } else {
                    this.resendCodeMessage =
                        'Code resent. Codes are valid for 5 min.';
                    this.errors = [];
                    return response;
                }
            }).catch((e) => {
                this.handleHttpStatus(e.response);
            });
        },
        verifyCode(otpEl) {
            this.errors = [];
            this.loading = true;
            Auth.verify2faQrCode(this.otpCode)
                .then(() => {
                    this.hideVerifyCodeActions = true;
                    this.loading = false;
                    Auth.getRecoveryCodes()
                        .then((response) => {
                            this.recoveryCodes = response.data;
                        })
                        .catch((e) => {
                            this.errors.push({
                                message: e.response.data.message,
                            });
                        });
                })
                .catch((e) => {
                    this.errors.push({ message: e.response.data.message });
                    this.loading = false;
                    otpEl.clearInput();
                });
        },
        setOtpCode(otpCode) {
            this.otpCode = otpCode;
        },
        enableTotp() {
            this.loading = true;
            const confirmPassword = Auth.confirmPassword(
                this.registerCreds.password
            );
            confirmPassword
                .then(() => {
                    const enable2Factor = Auth.enableTwoFactor();
                    enable2Factor
                        .then(() => {
                            Auth.get2faQrCode()
                                .then((response) => {
                                    this.qrCode = response.data.svg;
                                    this.loading = false;
                                })
                                .catch((e) => {
                                    this.loading = false;
                                    this.errors.push({
                                        message: e.response.data.message,
                                    });
                                });
                        })
                        .catch((e) => {
                            this.loading = false;
                            this.errors.push({
                                message: e.response.data.message,
                            });
                        });
                })
                .catch((e) => {
                    this.loading = false;
                    this.errors.push({
                        message: e.response.data.message,
                    });
                });
        },
        async copyCodes() {
            const str = this.recoveryCodes.join('\n');

            if (
                navigator &&
                navigator.clipboard &&
                navigator.clipboard.writeText
            ) {
                this.copied = true;
                return navigator.clipboard.writeText(str);
            }
            return Promise.reject('The Clipboard API is not available.');
        },
        downloadRecoveryCodes() {
            const str = this.recoveryCodes.join('\n');
            const blob = new Blob([str], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'my10x_recovery_codes.txt';
            a.click();
            window.URL.revokeObjectURL(url);
            this.downloaded = true;
        },
        setShowCancel2faModal(val) {
            this.showCancel2faModal = val;
        },
        completeTotpSetup() {
            location.href = '/welcome';
        },
        handleHttpStatus(error) {
            if (!error) return;

            const response = normalizeError(error);
            this.formErrors = {};

            // Handle validation errors
            if (
                response.status === 422 &&
                response.data &&
                response.data.errors
            ) {
                for (const key in response.data.errors) {
                    this.errors.push({ message: response.data.errors[key][0] });
                    this.formErrors[key] = response.data.errors[key][0];
                }
            }

            // Handle authentication errors
            if (response.status === 401 || response.status === 403) {
                location.href = '/login?loginstatus=autologout';
            }
        },
        setError(message) {
            this.errors = [];
            this.errors.push({ message: message });
        },
        setUtmParams(params) {
            this.utmParams = params;
            ApiService.apiCall('/utm-params', 'post', params);
        },
    },
});
